
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Target, Plus, ChevronRight, Clock, Trophy, 
  Users, AlertCircle, TrendingUp, Sparkles, 
  CheckCircle2, XCircle, ArrowUpRight, ShieldCheck,
  Calendar, Zap, Star, Filter
} from 'lucide-react';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
  CartesianGrid, Tooltip 
} from 'recharts';
import { User, Goal, GoalTemplate, GoalHealth } from './types';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { awardXP } from './services/gamificationService';

export const GoalsDashboard = ({ user, authReady }: { user: User, authReady: boolean }) => {
  const { t } = useTranslation();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [showWizard, setShowWizard] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [celebratingGoal, setCelebratingGoal] = useState<Goal | null>(null);

  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // 🔒 CRITICAL FIX: Verify user.id === auth.currentUser.uid BEFORE creating listener
    const authUid = auth.currentUser?.uid;
    if (!authUid || authUid !== user.id) {
      if (import.meta.env.DEV) {
        console.warn('[GOALS] onSnapshot blocked: user.id !== auth.currentUser.uid');
      }
      setGoals([]);
      return;
    }

    const q = query(
      collection(db, "users", user.id, "goals"),
      where("status", "==", activeTab)
    );
    
    // CRITICAL: Initialize as null, assign only if onSnapshot returns a function
    let unsubscribe: (() => void) | null = null;
    
    try {
      const snapshotResult = onSnapshot(q, (snap) => {
        setGoals(snap.docs.map(d => ({ id: d.id, ...d.data() } as Goal)));
      });
      if (typeof snapshotResult === 'function') {
        unsubscribe = snapshotResult;
      }
    } catch (initError: any) {
      if (import.meta.env.DEV) {
        console.error('[GOALS] Error initializing snapshot listener:', initError);
      }
    }
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [authReady, user.id, activeTab]);

  const handleCompleteGoal = async (goal: Goal) => {
    const goalRef = doc(db, "users", user.id, "goals", goal.id);
    await updateDoc(goalRef, {
      status: 'completed',
      completedAt: serverTimestamp(),
      currentValue: goal.targetValue
    });
    
    await awardXP(user.id, goal.xpReward, `Goal Completed: ${goal.title}`);
    setCelebratingGoal(goal);
  };

  return (
    <div className="p-4 md:p-8 pb-32 space-y-8 max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-black italic tracking-tighter flex items-center gap-3 uppercase">
            <Target className="text-emerald-400" size={40} /> {t('goals.title')}
          </h1>
          <p className="text-slate-500 text-sm font-medium uppercase tracking-widest">{t('goals.subtitle')}</p>
        </div>
        <button 
          onClick={() => setShowWizard(true)}
          className="group flex items-center gap-3 bg-white text-black px-8 py-4 rounded-[2rem] font-black shadow-lg hover:bg-emerald-500 hover:text-white transition-all"
        >
          <Plus size={20} /> {t('goals.create_challenge')}
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {goals.length === 0 ? (
            <div className="bg-slate-900/30 border-2 border-dashed border-slate-800 rounded-[3rem] p-20 text-center">
              <Target size={40} className="mx-auto opacity-20 mb-4" />
              <p className="text-slate-500 font-bold italic">{t('goals.no_missions')}</p>
            </div>
          ) : (
            goals.map(goal => (
              <div key={goal.id} className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-6">
                <h3 className="text-2xl font-black">{goal.title}</h3>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 uppercase text-xs font-black">{t('goals.progress')}</span>
                  <span className="text-emerald-400 font-black">{(goal.currentValue / goal.targetValue * 100).toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${(goal.currentValue / goal.targetValue * 100)}%` }} />
                </div>
                {goal.currentValue >= goal.targetValue && (
                  <button onClick={() => handleCompleteGoal(goal)} className="w-full bg-emerald-500 py-4 rounded-xl font-black">{t('goals.claim_reward')}</button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {showWizard && (
        <div className="fixed inset-0 z-[400] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-slate-900 p-10 rounded-[3rem] border border-slate-800 w-full max-w-md space-y-6">
              <h2 className="text-2xl font-black text-white uppercase">{t('goals.new_challenge')}</h2>
              <p className="text-slate-400 text-sm">{t('goals.wizard_coming_soon')}</p>
              <button 
                onClick={() => setShowWizard(false)} 
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-xl font-black transition-all"
              >
                {t('goals.got_it')}
              </button>
           </div>
        </div>
      )}
    </div>
  );
};
